import { Dangerous } from '@mui/icons-material'
import { Box, Divider, Grid, Skeleton, Typography } from '@mui/material'
import { debounce } from 'lodash'
import get from 'lodash/get'
import { useEffect, useState } from 'react'
import PlatformLogo from 'src/components/PlatformLogo'
import { DelIntelAgg, DelIntelThemeGroupby, PLATFORM } from 'src/services/openApiV2'
import { filterNames } from 'src/utils/config/config'
import { formatCurrency } from 'src/utils/functions'
import { pluralize } from 'src/utils/functions/pluralize'
import { ProblematicType } from '../Drawer/ErrorInsightsThemeDrawer'
import ErrorBreakdownPieChart from '../ErrorBreakdownPieChart'
import ErrorCard from '../ErrorCard'

interface ErrorInsightsSummaryProps {
  aggregateData: DelIntelAgg
  aggregateDataLoading: boolean
  themesData: DelIntelThemeGroupby[]
  themesDataLoading: boolean
  onClick: (theme: string) => void
}

const ErrorInsightsSummary = ({ aggregateData, aggregateDataLoading, themesData, themesDataLoading, onClick }: ErrorInsightsSummaryProps) => {
  const [breakdownCompWidth, setBreakdownCompWidth] = useState(0)

  useEffect(() => {
    const breakdownComp = document.getElementById('breakdown-comp')
    if (breakdownComp) {
      setBreakdownCompWidth(breakdownComp.clientWidth)
      const resizeObserver = new ResizeObserver(
        debounce(() => {
          setBreakdownCompWidth(Math.min(breakdownComp.clientWidth - 32, 500)) // 16px padding
        }, 300)
      )
      resizeObserver.observe(breakdownComp)
      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [])

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}>
      <Box
        display="flex"
        alignItems="center"
        gap={1}>
      </Box>

      <Grid
        container
        spacing={2}
        alignItems="stretch">
        <Grid
          item
          xs={12}
          md={6}>
          <Box
            id="breakdown-comp"
            border="1px solid #E6E6E6"
            borderRadius="4px"
            p={2}
            height="100%">
            <Typography
              fontSize={12}
              color="#000"
              fontWeight={600}>
              Breakdown
            </Typography>

            {themesDataLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                gap={4}>
                <Skeleton
                  variant="circular"
                  width={190}
                  height={190}
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="6px">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton
                      variant="rounded"
                      width={180}
                      height={17}
                      key={index}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <ErrorBreakdownPieChart
                themesData={themesData}
                breakdownCompWidth={breakdownCompWidth}
                onClick={onClick}
              />
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}>
          <Grid
            container
            spacing={1}
            sx={{ alignItems: 'stretch' }}>
            <Grid
              item
              xs={12}
              md={6}>
              <Box
                border="1px solid #E6E6E6"
                borderRadius="4px"
                p={2}
                display="flex"
                flexDirection="column"
                gap={1}
                height="100%">
                <Dangerous sx={{ fontSize: '20px', color: '#000' }} />
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.5}>
                  <Typography
                    fontSize={12}
                    color="#000">
                    Error charges
                  </Typography>
                  {aggregateDataLoading ? (
                    <Skeleton
                      variant="rounded"
                      width={130}
                      height={30}
                    />
                  ) : (
                    <Typography
                      fontSize={20}
                      color="#E52E2E"
                      fontWeight={600}
                      height="30px">
                      {formatCurrency(get(aggregateData, 'overall[0].error_charge', 0), { maxFractionDigits: 2 })}
                    </Typography>
                  )}
                  {aggregateDataLoading ? (
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={17}
                    />
                  ) : (
                    <Typography
                      fontSize={12}
                      color="#0008">
                      {get(aggregateData, 'overall[0].instances', 0)} {pluralize('Error', get(aggregateData, 'overall[0].instances', 0))}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <ErrorCard
                title="Top cause of errors"
                error={get(aggregateData, 'theme[0].name', '')}
                errorCharge={get(aggregateData, 'theme[0].error_charge', 0)}
                type={ProblematicType.THEME}
                loading={aggregateDataLoading}
                onClick={() => onClick(get(aggregateData, 'theme[0].name', ''))}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <ErrorCard
                title="Worst performing item"
                error={get(aggregateData, 'item[0].name', '')}
                errorCharge={get(aggregateData, 'item[0].error_charge', 0)}
                type={ProblematicType.ITEM}
                loading={aggregateDataLoading}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <ErrorCard
                title={`Worst performing ${filterNames.b_name.toLowerCase()}`}
                error={get(aggregateData, 'location[0].name', '')}
                errorCharge={get(aggregateData, 'location[0].error_charge', 0)}
                type={ProblematicType.LOCATION}
                loading={aggregateDataLoading}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ErrorInsightsSummary
